import CashflowAssistantApi from '@/api/CashflowAssistant';
import DDLogs from '@/DDLogs';

// The server is the source of truth (`hasUnreadCx` per conversation), so the
// badge survives a reload and shows for replies that arrived while the customer
// was logged out. Socket pushes only keep it live within a session.
const state = {
  hasUnread: false,
  // Which conversation the unread reply is in, so a client that was away when
  // it arrived can surface it rather than only showing a dot.
  unreadConversationId: null,
  // The conversation the open chat is showing, so a push about the thread the
  // customer is already reading does not raise a badge.
  openConversationId: null,
  initialised: false,
};

const getters = {
  hasUnreadCxMessage: state => state.hasUnread,
};

const actions = {
  // Once per session: the badge has to be correct on first paint.
  async initUnread({ state, commit, dispatch }) {
    if (state.initialised) {
      return;
    }
    commit('setInitialised', true);
    await dispatch('refreshUnread');
  },

  // Re-derives unread state from the server. The push path dies silently when
  // the socket exhausts its reconnection attempts (a websockets deploy is
  // enough), so anything that notices the tab waking up calls this.
  async refreshUnread({ commit }) {
    try {
      const { conversations } = await CashflowAssistantApi.getConversations();
      commit('setUnread', conversations || []);
    } catch (error) {
      DDLogs.error('[inline-cx] Failed to load unread chat state', { error });
    }
  },

  // Clears server-side too, so the badge does not return on the next load. The
  // flag is recomputed from the list afterwards, because another conversation
  // may still hold an unread reply.
  async markRead({ commit }, conversationId) {
    commit('setHasUnread', false);
    if (!conversationId) {
      return;
    }
    try {
      await CashflowAssistantApi.markConversationRead(conversationId);
      const { conversations } = await CashflowAssistantApi.getConversations();
      commit('setUnread', conversations || []);
    } catch (error) {
      DDLogs.error('[inline-cx] Failed to mark conversation read', { error });
    }
  },
};

const mutations = {
  setHasUnread(state, hasUnread) {
    state.hasUnread = hasUnread;
    if (!hasUnread) { state.unreadConversationId = null; }
  },
  setUnread(state, conversations) {
    const unread = conversations.find(c => c.hasUnreadCx);
    state.hasUnread = Boolean(unread);
    // The conversations route does not camel-case its payload — it only adds
    // hasUnreadCx — so the id arrives snake_cased. Both are read because the
    // rest of the chat API does convert.
    state.unreadConversationId = unread ? (unread.conversation_id || unread.conversationId) : null;
  },
  setOpenConversationId(state, conversationId) {
    state.openConversationId = conversationId;
  },
  setInitialised(state, initialised) {
    state.initialised = initialised;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
